import * as React from "react";
import { request } from "../../../../services/cpln";
import { useAppSelector } from "../../../../store/hooks";
import { selectLabel, selectValue } from "../../../../store/timezoneSlice";
import { BaseChart } from "../../BaseChart";
import {
  QueryRangeResponse,
  ChartDataPoint,
  toRegex,
  processSeriesData,
  getTimeRange,
  formatCount,
  getMetricsErrorMessage,
  buildByClause,
  translateGrouping,
} from "../../utils";

const EMPTY_ARRAY: string[] = [];

export interface DomainWarningsChartProps {
  org: string;
  location?: string[];
  grouping?: string[];
  hideFromLabel?: string[];
  fromIso?: string | null;
  toIso?: string | null;
  step?: string;
}

export const DomainWarningsChart: React.FC<DomainWarningsChartProps> = ({
  org,
  location = EMPTY_ARRAY,
  grouping = EMPTY_ARRAY,
  hideFromLabel = EMPTY_ARRAY,
  fromIso,
  toIso,
  step = "30s",
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<ChartDataPoint[]>([]);
  const [seriesKeys, setSeriesKeys] = React.useState<string[]>([]);

  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);

  React.useEffect(() => {
    if (org) {
      fetchMetrics();
    }
  }, [org, location, grouping, fromIso, toIso, step, timezoneValue]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationRegex = toRegex(location);
      const hasItemGrouping = grouping.includes("item");
      const translatedGrouping = translateGrouping(grouping, "domain");
      // When "item" is selected, also include type for more detail
      const effectiveGrouping = hasItemGrouping ? [...translatedGrouping, "type"] : translatedGrouping;
      const byClause = buildByClause(effectiveGrouping);
      const query = `sum(increase(domain_warnings{org=~"${org}", domain=~".*", type=~".*", location=~"${locationRegex}"}[15m])) ${byClause}`;

      const { start, end } = getTimeRange(fromIso, toIso);

      const response = await request<QueryRangeResponse>({
        service: "metrics",
        method: "post",
        url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(query)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
      });

      if (response.data.status !== "success") {
        setError((response.data as any).error || "Metrics API returned an error");
        return;
      }
      const result = response.data.data?.result || [];
      const { data: processedData, keys } = processSeriesData(result, {
        grouping: effectiveGrouping,
        timezoneValue,
        hideFromLabel,
      });
      setChartData(processedData);
      setSeriesKeys(keys);
    } catch (err: any) {
      console.error("Failed to fetch metrics:", err);
      setError(getMetricsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseChart
      title="Domain Warnings"
      loading={loading}
      error={error}
      chartData={chartData}
      seriesKeys={seriesKeys}
      formatYAxis={formatCount}
      formatTooltip={(value) => value.toFixed(0)}
      timezoneLabel={timezoneLabel}
    />
  );
};
